import { FormikTouched, useFormik } from "formik";
import { useGraphQLAuth } from "hooks/useGraphQLAuth";
import { useToast } from "hooks/useToast";
import { ChangeEvent, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";
import { PersonSelectPayload } from "../RegistrationForm/PersonSearchModal/usePersonSearch";

export interface RegistrationProps {
  customerCpf: string;
  importData?: {
    cpf?: string;
    email?: string;
    phone?: string;
  };
  profile: {
    name: string;
    company: string;
    position: string;
    bio: string;
    isSpeaker: boolean;
    isVisible: boolean;
    displayPosition?: number;
    photo?: {
      fileId?: string;
      fileUrl?: string;
      signedFileUrl?: string;
    };
  };
}

const initInputsValues: RegistrationProps = {
  customerCpf: "",
  importData: {
    email: "",
    phone: "",
  },
  profile: {
    name: "",
    company: "",
    position: "",
    bio: "",
    isSpeaker: false,
    isVisible: false,
    displayPosition: undefined,
  },
};

const initTouchedInputsValues: FormikTouched<RegistrationProps> = {
  customerCpf: false,
  importData: false,
  profile: {
    name: false,
    company: false,
    position: false,
    bio: false,
    isSpeaker: false,
    isVisible: false,
    displayPosition: false,
  },
};

function normalizePhone(phone: string): string {
  if (!phone) return phone;
  const digits = phone.replace(/\D/g, "");
  if (digits.startsWith("55")) return `+${digits}`;
  return `+55${digits}`;
}

export const useRegistrationNew = (eventId: string) => {
  const { setToast } = useToast();
  const { push } = useHistory();
  const { connectGraphQLApi, cancelRequest } = useGraphQLAuth();

  const [initialInputsValues] = useState(initInputsValues);
  const [isLoading, setIsLoading] = useState(false);
  const hasError = false;

  const {
    handleSubmit,
    handleChange: formikHandleChange,
    resetForm,
    setValues: setInputsValues,
    values: inputsValues,
    touched,
    errors,
  } = useFormik({
    initialValues: initInputsValues,
    initialTouched: initTouchedInputsValues,
    validationSchema: Yup.object({
      customerCpf: Yup.string()
        .optional()
        .test(
          "cpf-format",
          "CPF deve ter formato válido",
          (value) =>
            !value || /^\d{11}$|^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(value)
        ),
      importData: Yup.object({
        email: Yup.string()
          .optional()
          .test(
            "email-format",
            "Email deve ter formato válido",
            (value) => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
          ),
        phone: Yup.string()
          .optional()
          .test(
            "phone-format",
            "Telefone deve ter formato válido",
            (value) => !value || /^[\d\s()+-]+$/.test(value)
          ),
      }).optional(),
      profile: Yup.object({
        name: Yup.string().required("Nome é obrigatório"),
        company: Yup.string().required("Empresa é obrigatória"),
        position: Yup.string().required("Cargo é obrigatório"),
        bio: Yup.string().required("Biografia é obrigatória"),
        isSpeaker: Yup.boolean().optional(),
        isVisible: Yup.boolean().optional(),
        displayPosition: Yup.number()
          .optional()
          .nullable()
          .min(1, "Ordem deve ser maior que 0")
          .integer("Ordem deve ser um número inteiro"),
      }),
    }).test(
      "at-least-one-identifier",
      "Preencha pelo menos um: CPF, email ou telefone",
      (values) => {
        const hasCpf = !!values?.customerCpf;
        const hasEmail = !!values?.importData?.email;
        const hasPhone = !!values?.importData?.phone;
        return hasCpf || hasEmail || hasPhone;
      }
    ),
    onSubmit: async (_) => {
      await sendData();
      push(`/events/${eventId}?tab=Inscrições`);
    },
  });

  /* HOOKS */
  useEffect(() => {
    return () => {
      cancelRequest();
    };
  }, [cancelRequest]);

  useEffect(() => {
    setInputsValues(initialInputsValues);
  }, [initialInputsValues, setInputsValues]);

  /* EVENT HANDLERS */
  const handlePersonSelect = ({ customer, unmasked }: PersonSelectPayload) => {
    setInputsValues((prev) => ({
      ...prev,
      customerCpf: unmasked.cpf || prev.customerCpf,
      importData: {
        ...prev.importData,
        email: unmasked.email || prev.importData?.email,
        phone: unmasked.phoneNumber || prev.importData?.phone,
      },
      profile: {
        ...prev.profile,
        name: customer.name || prev.profile.name,
      },
    }));
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;

    if (type === "checkbox") {
      if (name.includes(".")) {
        const [parent, child] = name.split(".");
        setInputsValues((prev) => ({
          ...prev,
          [parent]: {
            ...(prev[parent as keyof RegistrationProps] as any),
            [child]: checked,
          },
        }));
      } else {
        setInputsValues((prev) => ({
          ...prev,
          [name]: checked,
        }));
      }
    } else {
      if (name.includes(".")) {
        const [parent, child] = name.split(".");
        setInputsValues((prev) => ({
          ...prev,
          [parent]: {
            ...(prev[parent as keyof RegistrationProps] as any),
            [child]: value,
          },
        }));
      } else {
        formikHandleChange(event);
      }
    }
  };

  /* API CALLS */

  const sendData = async (): Promise<string | null> => {
    setIsLoading(true);

    const mutation = `
      mutation CreateRegistrationFromBackoffice($input: BackofficeRegistrationInput!) {
        backofficeCreateRegistration(input: $input) {
          registrationId
          profile {
            bio
            isSpeaker
            name
            company
          }
        }
      }
    `;

    const cleanCpf = inputsValues.customerCpf
      ? inputsValues.customerCpf.replace(/\D/g, "")
      : undefined;

    const profileInput: any = {
      bio: inputsValues.profile.bio,
      company: inputsValues.profile.company,
      isSpeaker: inputsValues.profile.isSpeaker,
      isVisible: inputsValues.profile.isVisible,
      name: inputsValues.profile.name,
      position: inputsValues.profile.position,
    };

    if (
      inputsValues.profile.displayPosition !== undefined &&
      inputsValues.profile.displayPosition !== null
    ) {
      profileInput.displayPosition = parseInt(
        inputsValues.profile.displayPosition.toString()
      );
    }

    const importDataInput: any = {};
    if (inputsValues.importData?.email) {
      importDataInput.email = inputsValues.importData.email;
    }
    if (inputsValues.importData?.phone) {
      importDataInput.phone = normalizePhone(inputsValues.importData.phone);
    }
    if (cleanCpf) {
      importDataInput.cpf = cleanCpf;
    }

    const variables = {
      input: {
        eventId,
        ...(cleanCpf && { customerCpf: cleanCpf }),
        profile: profileInput,
        ...(Object.keys(importDataInput).length > 0 && {
          importData: importDataInput,
        }),
      },
    };

    try {
      const response = await connectGraphQLApi({
        query: mutation,
        variables,
        errorMessage: "Erro ao criar inscrição",
        useNewApi: true,
      });

      setToast({
        type: "success",
        message: "Inscrição criada com sucesso!",
        isActive: true,
        timeToRemoveToast: 3000,
      });
      return response?.data?.backofficeCreateRegistration?.registrationId;
    } catch (error: any) {
      console.error("[sendData] Error:", error);
      setToast({
        type: "error",
        message: error?.message || "Erro ao criar inscrição.",
        isActive: true,
        timeToRemoveToast: 5000,
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    handleChange,
    handlePersonSelect,
    inputsValues,
    touched,
    errors,
    handleSubmit,
    isLoading,
    hasError,
    resetForm,
  };
};
